/**
 * Référentiel « RÈGLES DES MENUS » (outil interne MOA / recette / dev), distinct du mode
 * « Règles de gestion » (reglesGestion.ts) : documente, pour chaque entrée du menu latéral, la
 * condition qui détermine réellement si elle s'affiche — profil du compte, droit, ou aucune.
 * Source unique : src/components/layout/store/state.ts (MENU_CLIENT, MENU_FOURNISSEUR...),
 * src/components/layout/Layout.vue (filtre satisfaction), src/router/index.ts (meta.droit).
 * Contenu volontairement hors i18n : documentation interne, non traduite, non poussée en base.
 */

export interface RegleMenu {
    titre: string;
    href: string;
    condition: string;
    source: string;
}

export interface GroupeMenu {
    titre: string;
    items: RegleMenu[];
}

export const REGLES_MENUS: GroupeMenu[] = [
    {
        titre: 'Choix du jeu de menus (avant même le détail ci-dessous)',
        items: [
            {
                titre: 'Menu complet "client" (celui détaillé plus bas)',
                href: '—',
                condition: 'Compte avec isClient = true et isFournisseur = false.',
                source: 'store/state.ts (updateMenuLabels, MENU_CLIENT)',
            },
            {
                titre: 'Menu réduit "fournisseur" (Dashboard, Factures, Règlements)',
                href: '—',
                condition: 'Compte avec isFournisseur = true et isClient = false.',
                source: 'store/state.ts (updateMenuLabels, MENU_FOURNISSEUR)',
            },
            {
                titre: 'Menu "client" ou "fournisseur" + un lien "Changer de portail" en tête',
                href: '—',
                condition: 'Compte ayant les deux profils à la fois (isClient ET isFournisseur) : le jeu de menus suit le portail actif (getPortailName), avec un lien supplémentaire pour basculer vers l\'autre.',
                source: 'store/state.ts (updateMenuLabels, MENU_CLIENT_SWITCH_FOURNISSEUR / MENU_FOURNISSEUR_SWITCH_CLIENT)',
            },
        ],
    },
    {
        titre: 'Menu client — entrées de premier niveau',
        items: [
            { titre: 'Dashboard', href: '/dashboard', condition: 'Aucune : toujours visible pour un compte client.', source: 'store/state.ts (MENU_CLIENT)' },
            { titre: 'Interventions (et ses sous-entrées)', href: '—', condition: 'Aucune : le groupe et toutes ses sous-entrées sont toujours visibles, y compris Rapports à signer et Planning préventif, qui portent pourtant un droit déclaré (voir § droits déclarés mais non appliqués).', source: 'store/state.ts (MENU_CLIENT) ; router/index.ts (meta.droit non consommé par beforeEnter)' },
            { titre: 'Devis (et ses sous-entrées)', href: '—', condition: 'Aucune : toujours visible.', source: 'store/state.ts (MENU_CLIENT)' },
            { titre: 'Documents (et ses sous-entrées)', href: '—', condition: 'Aucune : toujours visible.', source: 'store/state.ts (MENU_CLIENT)' },
            { titre: 'Administratif (et ses sous-entrées)', href: '—', condition: 'Aucune : toujours visible.', source: 'store/state.ts (MENU_CLIENT)' },
            { titre: 'Facturation', href: '/facturation', condition: 'Aucune en réalité : le lien est toujours visible et accessible, bien que la page porte un droit déclaré (consulter.facture.recue) — voir § droits déclarés mais non appliqués. Seul le widget équivalent du tableau de bord applique vraiment ce droit.', source: 'store/state.ts (MENU_CLIENT) ; router/index.ts (/facturation, meta.droit non consommé) ; HomeMain.vue (hasRightFinancier, lui, applique le droit)' },
            { titre: 'Votre suivi énergétique (et ses sous-entrées)', href: '—', condition: 'Aucune : toujours visible (Noe 3 et Noe EcoEnergie sont des liens externes, sans condition non plus).', source: 'store/state.ts (MENU_CLIENT)' },
            { titre: 'Réclamations (et ses sous-entrées)', href: '—', condition: 'Aucune : toujours visible.', source: 'store/state.ts (MENU_CLIENT)' },
            { titre: 'Satisfaction', href: '/satisfaction', condition: 'Seule entrée réellement filtrée dynamiquement : retirée du menu si le paramétrage du compte (MODALE_SATISFACTION_ACTIVE, par compte et par domaine) ne l\'autorise pas.', source: 'Layout.vue (menu(), filtre sur modalConditionDTO.active)' },
            { titre: 'Contacts', href: '/contacts', condition: 'Aucune : toujours visible.', source: 'store/state.ts (MENU_CLIENT)' },
        ],
    },
    {
        titre: 'Droits déclarés sur la route, mais non appliqués au menu ni à la navigation actuellement utilisée',
        items: [
            {
                titre: 'Voir toutes les demandes, Rapports à signer, Planning préventif, Interventions préventives/non préventives, Facturation',
                href: '—',
                condition: 'Ces routes déclarent un meta.droit (consulter.demande, consulter.interventionP, consulter.interventionNP, consulter.facture.recue) mais leur beforeEnter ne vérifie que le rôle client (Permissions.CLIENT) : le droit déclaré n\'est ni utilisé pour masquer l\'entrée de menu (barre latérale), ni pour bloquer la navigation. Un compte client peut donc toujours cliquer sur ces liens dans le menu latéral, qu\'il ait le droit métier ou non — la restriction, si elle existe, vient alors uniquement du BFF/API.',
                source: 'router/index.ts (ces routes, beforeEnter: Permissions.userCanAccess(Permissions.CLIENT) uniquement) ; router/AccessControl.ts (userCanAccess)',
            },
            {
                titre: 'Important : un filtrage par droit correct existe, mais dans un composant mort',
                href: '—',
                condition: 'MainNavigation.vue implémente une navigation en survol (NavBlock) qui, elle, vérifie correctement chaque droit un par un (consulter.demande, consulter.interventionP, consulter.interventionNP, creer.demande pour le "+" Nouvelle demande...). Mais ce composant n\'est importé par aucun autre fichier du projet : il n\'est jamais monté, donc jamais exécuté. C\'est très probablement un ancien menu (dropdown en survol) remplacé par le menu latéral actuel (Layout.vue, sidebar-menu), dont le filtrage par droit n\'a pas été repris lors de la bascule. À faire trancher : soit réactiver ce filtrage sur le menu actuel, soit considérer que c\'est le BFF/API qui protège, et supprimer ce code mort.',
                source: 'components/layout/MainNavigation.vue (jamais importé ailleurs dans src/)',
            },
        ],
    },
    {
        titre: 'Boutons de raccourci (en-tête, hors menu latéral)',
        items: [
            {
                titre: 'DEMANDER UN DEVIS',
                href: '/devis',
                condition: 'Portail client uniquement (pas fournisseur). Aucun droit métier vérifié.',
                source: 'MainHeader.vue (bouton-demande-devis, isPortailClient)',
            },
            {
                titre: "DEMANDE D'INTERVENTION",
                href: '/intervention',
                condition: 'Portail client uniquement, ET droit creer.demande requis (checkDroitSubRoute). C\'est le seul endroit de la navigation réellement utilisée qui vérifie encore ce droit.',
                source: 'MainHeader.vue (bouton-demande-intervention, checkDroitSubRoute(\'creer.demande\'), isPortailClient)',
            },
        ],
    },
    {
        titre: 'Menu fournisseur — entrées de premier niveau',
        items: [
            { titre: 'Dashboard', href: '/dashboard-fournisseur', condition: 'Droit role.fournisseur, réellement vérifié par le routeur (beforeEnter).', source: 'router/index.ts (/dashboard-fournisseur, Permissions.FOURNISSEUR)' },
            { titre: 'Factures', href: '/factures-fournisseur', condition: 'Droit role.fournisseur, réellement vérifié par le routeur.', source: 'router/index.ts (/factures-fournisseur, Permissions.FOURNISSEUR)' },
            { titre: 'Règlements', href: '/reglements-fournisseur', condition: 'Droit role.fournisseur, réellement vérifié par le routeur.', source: 'router/index.ts (/reglements-fournisseur, Permissions.FOURNISSEUR)' },
        ],
    },
];
